import { apiConfig } from '../config/api-config';

export interface DailyReportConfig {
  scheduleTime: string;
  timezone: string;
  recipients: string[];
  enabled: boolean;
  lastSent?: string;
  nextScheduled?: string;
  cronJobId?: string;
  settings: {
    emailTemplate: string;
    includeCharts: boolean;
    maxRetries: number;
    retryInterval: number;
  };
  updatedAt?: string;
  updatedBy?: string;
}

export interface TestReportResult {
  success: boolean;
  message: string;
  data?: any;
  emailResults?: Array<{
    recipient: string;
    success: boolean;
    error?: string;
  }>;
  config?: any;
}

class DailyReportService {
  private baseUrl = apiConfig.baseUrl;

  /**
   * Get the current daily report configuration
   */
  async getConfig(): Promise<DailyReportConfig> {
    try {
      console.log('📖 DailyReportService: Fetching configuration');

      const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.dailyReportConfig}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const config = await response.json();
      console.log('✅ DailyReportService: Configuration loaded successfully');

      return config;
    } catch (error) {
      console.error('❌ DailyReportService: Failed to load configuration:', error);
      throw error;
    }
  }

  /**
   * Save the daily report configuration
   */
  async saveConfig(config: DailyReportConfig): Promise<{ message: string; config: any }> {
    try {
      console.log('💾 DailyReportService: Saving configuration');

      const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.saveDailyReportConfig}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.validationErrors) {
          throw new Error(`Validation failed: ${errorData.validationErrors.join(', ')}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ DailyReportService: Configuration saved successfully');

      return result;
    } catch (error) {
      console.error('❌ DailyReportService: Failed to save configuration:', error);
      throw error;
    }
  }

  /**
   * Send a test daily report
   */
  async testReport(): Promise<TestReportResult> {
    try {
      console.log('🧪 DailyReportService: Sending test report');

      const response = await fetch(`${this.baseUrl}${apiConfig.endpoints.testDailyReport}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ DailyReportService: Test report sent successfully');

      return result;
    } catch (error) {
      console.error('❌ DailyReportService: Failed to send test report:', error);
      throw error;
    }
  }

  /**
   * Validate configuration before saving
   */
  validateConfig(config: DailyReportConfig): string[] {
    const errors: string[] = [];

    // Validate schedule time format (HH:MM 24-hour)
    if (!config.scheduleTime || !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(config.scheduleTime)) {
      errors.push("Invalid schedule time format. Use HH:MM (24-hour format, e.g., 06:00, 18:30)");
    }

    // Validate timezone
    const validTimezones = ['Pacific/Auckland', 'UTC'];
    if (!config.timezone || !validTimezones.includes(config.timezone)) {
      errors.push(`Invalid timezone. Must be one of: ${validTimezones.join(', ')}`);
    }

    // Validate recipients
    if (!config.recipients || !Array.isArray(config.recipients) || config.recipients.length === 0) {
      errors.push("At least one recipient email is required");
    } else {
      // Validate email formats
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      config.recipients.forEach((email, index) => {
        if (!email || typeof email !== 'string') {
          errors.push(`Recipient ${index + 1}: Email is required and must be a string`);
        } else if (!emailRegex.test(email.trim())) {
          errors.push(`Recipient ${index + 1}: Invalid email format: ${email.trim()}`);
        }
      });

      // Check for duplicates
      const uniqueEmails = [...new Set(config.recipients.map(email => email.trim().toLowerCase()))];
      if (uniqueEmails.length !== config.recipients.length) {
        errors.push("Duplicate email addresses found. Please remove duplicates.");
      }
    }

    // Validate settings
    if (config.settings) {
      if (config.settings.maxRetries < 0 || config.settings.maxRetries > 10) {
        errors.push("Max retries must be between 0 and 10");
      }

      if (config.settings.retryInterval < 60 || config.settings.retryInterval > 3600) {
        errors.push("Retry interval must be between 60 and 3600 seconds (1-60 minutes)");
      }

      const validTemplates = ['default', 'minimal', 'detailed'];
      if (!validTemplates.includes(config.settings.emailTemplate)) {
        errors.push(`Invalid email template. Must be one of: ${validTemplates.join(', ')}`);
      }
    }

    return errors;
  }

  /**
   * Get default configuration
   */
  getDefaultConfig(): DailyReportConfig {
    return {
      scheduleTime: '06:00',
      timezone: 'Pacific/Auckland',
      recipients: ['geoff@emertech.nz'],
      enabled: true,
      settings: {
        emailTemplate: 'default',
        includeCharts: true,
        maxRetries: 3,
        retryInterval: 300
      },
      updatedAt: new Date().toISOString(),
      updatedBy: 'system'
    };
  }

  /**
   * Format configuration for display
   */
  formatConfigForDisplay(config: DailyReportConfig): any {
    return {
      ...config,
      lastSentFormatted: config.lastSent ? new Date(config.lastSent).toLocaleString() : 'Never',
      nextScheduledFormatted: config.nextScheduled ? new Date(config.nextScheduled).toLocaleString() : 'Not scheduled',
      recipientsCount: config.recipients.length,
      status: config.enabled ? 'Enabled' : 'Disabled',
      statusColor: config.enabled ? 'success' : 'error'
    };
  }

  /**
   * Get Firebase scheduler status information
   */
  getSchedulerStatus(config: DailyReportConfig): {
    status: string;
    description: string;
    nextCheck: string;
  } {
    const status = config.enabled ? 'Active' : 'Disabled';
    const description = config.enabled
      ? `Scheduler runs every 15 minutes, sends reports at ${config.scheduleTime} ${config.timezone}`
      : 'Scheduler is disabled';

    // Calculate next 15-minute interval
    const now = new Date();
    const minutes = now.getMinutes();
    const next15Min = Math.ceil(minutes / 15) * 15;
    const nextCheck = new Date(now);

    if (next15Min >= 60) {
      nextCheck.setHours(nextCheck.getHours() + 1, 0, 0, 0);
    } else {
      nextCheck.setMinutes(next15Min, 0, 0);
    }

    return {
      status,
      description,
      nextCheck: nextCheck.toLocaleString()
    };
  }
}

// Export singleton instance
export const dailyReportService = new DailyReportService();
export default dailyReportService;
